import { computed, inject, Injectable, signal } from '@angular/core';
import { ConnectivityService } from '../connectivity/connectivity';
import type { OfflineBookRecord, OfflineSnapshot } from './offline-db';

export type OfflineSessionMode = 'online' | 'offline';

/** Why an offline open cannot show the book. */
export type OfflineBlockReason =
  | 'notDownloaded'
  | 'expired'
  | 'clockRolledBack'
  /** Extension link (`?f=plugin`) opened with connection: its token is no longer valid. */
  | 'offlineOnlyLink';

/**
 * Whether the reader was opened with or without connection. As in NR1 the mode is fixed when the
 * book opens: losing (or recovering) the connection afterwards does not switch it, the reader
 * asks the user to wait or to reopen the book instead.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineSessionService {
  private readonly connectivity = inject(ConnectivityService);

  private readonly currentMode = signal<OfflineSessionMode | undefined>(undefined);
  private readonly currentBook = signal<OfflineBookRecord | undefined>(undefined);
  private readonly currentSnapshot = signal<OfflineSnapshot | undefined>(undefined);
  private readonly currentDownloadedLoan = signal<string | undefined>(undefined);
  private readonly currentBlockReason = signal<OfflineBlockReason | undefined>(undefined);
  /** Browser connectivity when the book opened: an offline open may happen with a network "up". */
  private readonly connectivityAtOpen = signal<boolean | undefined>(undefined);

  readonly mode = this.currentMode.asReadonly();
  /** Downloaded book this offline session reads from. */
  readonly book = this.currentBook.asReadonly();
  /** Reader data of the offline session, with the changes made offline. */
  readonly snapshot = this.currentSnapshot.asReadonly();
  /** Loan open online that is also downloaded in this browser: its copy is kept up to date. */
  readonly downloadedLoan = this.currentDownloadedLoan.asReadonly();
  readonly blockReason = this.currentBlockReason.asReadonly();
  readonly isOffline = computed(() => this.currentMode() === 'offline');
  readonly connectionChanged = computed(() => {
    const atOpen = this.connectivityAtOpen();
    if (atOpen === undefined) return false;
    // Opened online with the browser reporting no network: that report cannot be trusted, so only
    // a loss seen after a reported connection counts.
    if (this.currentMode() === 'online') return atOpen && !this.connectivity.isOnline();
    return this.connectivity.isOnline() !== atOpen;
  });

  startOnline(): void {
    this.currentMode.set('online');
    this.currentBook.set(undefined);
    this.connectivityAtOpen.set(this.connectivity.isOnline());
  }

  startOffline(book: OfflineBookRecord, snapshot: OfflineSnapshot): void {
    this.currentMode.set('offline');
    this.currentBook.set(book);
    this.currentSnapshot.set(snapshot);
    this.connectivityAtOpen.set(this.connectivity.isOnline());
  }

  updateSnapshot(snapshot: OfflineSnapshot): void {
    this.currentSnapshot.set(snapshot);
  }

  setDownloadedLoan(loanKey: string | undefined): void {
    this.currentDownloadedLoan.set(loanKey);
  }

  block(reason: OfflineBlockReason): void {
    this.currentBlockReason.set(reason);
  }
}
